import React, { useState, useEffect, useRef } from "react";
import {
  Camera,
  RefreshCw,
  X,
  MapPin,
  AlertCircle,
  Upload,
  Briefcase,
  CheckCircle2,
  Building,
  Home,
  Globe,
  Loader2,
  LogOut,
} from "lucide-react";
import { postCheckIn, postCheckOut } from "../ApiHelper/url_Helpers";
import { toasterError, toasterSuccess } from "./toaster";
import hourlyPunchService from "../services/hourlyPunchService";
import liveTrackingService from "../services/liveTrackingService";

const CheckInModal = ({
  isOpen,
  onClose,
  onSuccess,
  defaultLocationName = "",
  defaultWorkMode = "office",
  type = "check-in",
  mode,
  // When true  → selfie capture is required (camera + upload shown)
  // When false → selfie section is hidden; submit without photo
  faceVerificationRequired = true,
}) => {
  const isCheckOut =
    type === "check-out" ||
    type === "checkOut" ||
    mode === "check-out" ||
    mode === "checkOut";

  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState("");
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Geolocation state
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState("idle"); // idle, loading, success, error
  const [locationError, setLocationError] = useState("");
  const [coords, setCoords] = useState({
    latitude: "",
    longitude: "",
    accuracy: "",
  });
  const [locationName, setLocationName] = useState(defaultLocationName || "");
  const [workMode, setWorkMode] = useState(defaultWorkMode || "office");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Stop camera tracks cleanly — kills every track, clears the ref,
  // and cancels any in-flight getUserMedia call.
  const stopCamera = () => {
    // Cancel a pending getUserMedia if it hasn't resolved yet
    if (streamRef._cancelStart) {
      streamRef._cancelStart();
      streamRef._cancelStart = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStream(null);
    }
    // Clear the video element's srcObject so the browser releases the device
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Start live webcam stream.
  // Uses a cancellation flag so that if the modal closes while getUserMedia
  // is still pending the acquired stream is immediately killed (fixes the
  // "camera light stays on after close" bug).
  const startCamera = async () => {
    setCameraError("");
    stopCamera();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError("Camera is not supported on this browser or device.");
      return;
    }

    // Track whether this particular startCamera call was cancelled
    let cancelled = false;
    // Expose a cancel fn on the ref so stopCamera / cleanup can reach it
    streamRef._cancelStart = () => { cancelled = true; };

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });

      // If the modal was closed (or stopCamera was called) while we were
      // awaiting getUserMedia, immediately stop the acquired stream.
      if (cancelled) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = mediaStream;
      streamRef._cancelStart = null;
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      if (cancelled) return; // ignore errors after cancellation
      console.warn("Camera start error:", err);
      let msg = "Unable to access camera. Please allow camera permissions.";
      if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        msg = "No camera found on this device. You can upload a photo instead.";
      } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        msg = "Camera permission was denied. Please allow camera access in browser settings, or upload a photo.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        msg = "Camera is currently in use by another application.";
      }
      setCameraError(msg);
    }
  };

  // Reverse geocode using BigDataCloud / Nominatim with graceful fallback
  const fetchReverseGeocode = async (lat, lon) => {
    try {
      const bdcUrl = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
      const res = await fetch(bdcUrl);
      if (res.ok) {
        const data = await res.json();
        const parts = [
          data.locality || data.city,
          data.principalSubdivision,
          data.countryName,
        ].filter(Boolean);
        if (parts.length > 0) {
          const resolvedName = parts.join(", ");
          setLocationName(resolvedName);
          return;
        }
      }
    } catch (e) {
      console.warn("BigDataCloud reverse geocode error, trying OSM fallback:", e);
    }

    try {
      const osmUrl = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`;
      const res = await fetch(osmUrl, {
        headers: { "Accept-Language": "en" },
      });
      if (res.ok) {
        const data = await res.json();
        const addr = data.address || {};
        const parts = [
          addr.suburb || addr.neighbourhood || addr.road,
          addr.city || addr.town || addr.village || addr.county,
          addr.state,
        ].filter(Boolean);
        if (parts.length > 0) {
          setLocationName(parts.join(", "));
          return;
        }
      }
    } catch (e) {
      console.warn("Nominatim reverse geocode error:", e);
    }

    // Default fallback if reverse geocoding returns nothing
    if (!locationName && defaultLocationName) {
      setLocationName(defaultLocationName);
    }
  };

  // Fetch Geolocation
  const requestLocation = () => {
    setLocationLoading(true);
    setLocationStatus("loading");
    setLocationError("");

    if (!navigator.geolocation) {
      setLocationStatus("error");
      setLocationError("Geolocation is not supported by your browser.");
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setCoords({
          latitude: latitude.toString(),
          longitude: longitude.toString(),
          accuracy: Math.round(accuracy).toString(),
        });
        setLocationStatus("success");
        setLocationLoading(false);
        fetchReverseGeocode(latitude, longitude);
      },
      (err) => {
        console.warn("Geolocation error:", err);
        setLocationLoading(false);
        setLocationStatus("error");
        let msg = "Could not fetch GPS location.";
        if (err.code === 1) {
          msg = "Location permission denied. Please allow location access in your browser.";
        } else if (err.code === 2) {
          msg = "Location unavailable. Please check your network or GPS.";
        } else if (err.code === 3) {
          msg = "Location request timed out. Please try refreshing.";
        }
        setLocationError(msg);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  // Mount / Open effects
  useEffect(() => {
    if (isOpen) {
      // Only start camera when face verification is required
      if (faceVerificationRequired) {
        startCamera();
      }
      requestLocation();
      if (defaultLocationName && !locationName) {
        setLocationName(defaultLocationName);
      }
      if (defaultWorkMode) {
        setWorkMode(defaultWorkMode.toLowerCase());
      }
    } else {
      stopCamera();
      setSelfieFile(null);
      if (selfiePreview) {
        URL.revokeObjectURL(selfiePreview);
        setSelfiePreview(null);
      }
      setIsSubmitting(false);
      setCameraError("");
      setLocationError("");
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, faceVerificationRequired]);

  // Safety net: guarantee camera is stopped when the component unmounts,
  // no matter how the parent removes it from the tree.
  useEffect(() => {
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-attach video stream when stream or video ref updates
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Capture photo from video feed
  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement("canvas");

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Draw current frame
    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toasterError("Failed to capture image. Please try again.");
          return;
        }
        const file = new File([blob], `selfie_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setSelfieFile(file);
        if (selfiePreview) {
          URL.revokeObjectURL(selfiePreview);
        }
        const previewUrl = URL.createObjectURL(blob);
        setSelfiePreview(previewUrl);
        // Stop camera while viewing preview
        stopCamera();
      },
      "image/jpeg",
      0.9
    );
  };

  // Retake photo
  const retakePhoto = () => {
    if (selfiePreview) {
      URL.revokeObjectURL(selfiePreview);
      setSelfiePreview(null);
    }
    setSelfieFile(null);
    startCamera();
  };

  // Manual photo upload fallback
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toasterError("Please upload a valid image file.");
      return;
    }

    setSelfieFile(file);
    if (selfiePreview) {
      URL.revokeObjectURL(selfiePreview);
    }
    const previewUrl = URL.createObjectURL(file);
    setSelfiePreview(previewUrl);
    stopCamera();
  };

  // Handle final submission (Check-in or Check-out)
  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Only enforce selfie requirement when face_verification_required is true
    if (faceVerificationRequired && !selfieFile) {
      toasterError(
        isCheckOut
          ? "Selfie photo is required to check out. Please capture or upload a selfie."
          : "Selfie photo is required to check in. Please capture or upload a selfie."
      );
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      // Append selfie only if face verification is required and a file was captured
      if (faceVerificationRequired && selfieFile) {
        formData.append("selfie", selfieFile, selfieFile.name || "selfie.jpg");
      }
      formData.append("latitude", coords.latitude || "");
      formData.append("longitude", coords.longitude || "");
      formData.append("accuracy", coords.accuracy || "0");
      formData.append("location_name", locationName.trim() || defaultLocationName || "Office Location");
      formData.append("work_mode", workMode || "office");

      const apiAction = isCheckOut ? postCheckOut : postCheckIn;
      const response = await apiAction(formData);
      toasterSuccess(
        response?.message || (isCheckOut ? "Check-out successful!" : "Check-in successful!")
      );

      if (onSuccess) {
        onSuccess(response);
      }
      handleClose();
      if (isCheckOut) {
        hourlyPunchService.stop();
        liveTrackingService.stop();
      } else {
        if (hourlyPunchService.isEnabled()) {
          hourlyPunchService.start(new Date().toISOString());
        } else {
          hourlyPunchService.stop();
        }
        liveTrackingService.start();
      }
    } catch (err) {
      console.error(isCheckOut ? "Check-out error:" : "Check-in error:", err);
      toasterError(
        err?.message || (isCheckOut ? "Check-out failed. Please try again." : "Check-in failed. Please try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    if (selfiePreview) {
      URL.revokeObjectURL(selfiePreview);
      setSelfiePreview(null);
    }
    setSelfieFile(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 my-auto transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-cyan-50 text-cyan-600">
              {isCheckOut ? (
                <LogOut className="w-5 h-5" />
              ) : (
                <Camera className="w-5 h-5" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {isCheckOut ? "Check-Out Verification" : "Check-In Verification"}
              </h2>
              <p className="text-xs text-gray-500">
                {isCheckOut
                  ? faceVerificationRequired
                    ? "Capture a selfie and confirm your location to punch out"
                    : "Confirm your location to punch out"
                  : faceVerificationRequired
                  ? "Capture a selfie and confirm your location to punch in"
                  : "Confirm your location to punch in"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Main Grid: conditionally 2-col (with selfie) or 1-col (no selfie) */}
          <div
            className={`grid gap-6 ${
              faceVerificationRequired ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
            }`}
          >
            {/* ── Selfie Section ── only when face verification is required */}
            {faceVerificationRequired && (
              <div className="flex flex-col items-center">
                <div className="w-full relative aspect-[4/3] bg-gray-900 rounded-xl overflow-hidden shadow-inner border border-gray-200 flex items-center justify-center">
                  {selfiePreview ? (
                    // Preview Captured Image
                    <div className="relative w-full h-full">
                      <img
                        src={selfiePreview}
                        alt="Selfie Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 left-2 bg-emerald-500/90 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Photo Captured
                      </div>
                    </div>
                  ) : cameraError ? (
                    // Camera Error / Permission Denied State
                    <div className="p-4 text-center text-white space-y-3">
                      <AlertCircle className="w-10 h-10 text-amber-400 mx-auto" />
                      <p className="text-xs text-gray-300 leading-relaxed px-2">
                        {cameraError}
                      </p>
                      <div className="flex flex-col gap-2 pt-2">
                        <button
                          type="button"
                          onClick={startCamera}
                          className="text-xs bg-gray-800 hover:bg-gray-700 text-white font-medium py-1.5 px-3 rounded-lg border border-gray-600 transition inline-flex items-center justify-center gap-1.5"
                        >
                          <RefreshCw className="w-3.5 h-3.5" /> Try Again
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Live Camera Stream
                    <div className="relative w-full h-full flex items-center justify-center">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover -scale-x-100"
                      />
                      <div className="absolute top-2 left-2 bg-black/60 text-white text-[11px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1.5 backdrop-blur-xs">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        Live Camera
                      </div>
                      {/* Guiding Oval Overlay */}
                      <div className="absolute inset-0 pointer-events-none border-2 border-dashed border-white/40 rounded-full m-8" />
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>

                {/* Action Buttons for Camera */}
                <div className="w-full flex items-center justify-between gap-2 mt-3">
                  {selfiePreview ? (
                    <button
                      type="button"
                      onClick={retakePhoto}
                      className="w-full py-2 px-3 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center gap-1.5"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Retake Photo
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={!!cameraError}
                        onClick={capturePhoto}
                        className="flex-1 py-2 px-3 text-xs font-semibold text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition flex items-center justify-center gap-1.5"
                      >
                        <Camera className="w-3.5 h-3.5" /> Capture Selfie
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        title="Upload photo from device"
                        className="py-2 px-3 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center gap-1"
                      >
                        <Upload className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
              </div>
            )}

            {/* ── Location & Work Details Section ── */}
            <div className="flex flex-col justify-between space-y-4">
              {/* Geolocation Card */}
              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 uppercase tracking-wider">
                    <MapPin className="w-4 h-4 text-cyan-600" /> Current Location
                  </div>
                  <button
                    type="button"
                    onClick={requestLocation}
                    disabled={locationLoading}
                    className="text-xs text-cyan-600 hover:text-cyan-700 font-medium inline-flex items-center gap-1 transition"
                  >
                    {locationLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    Refresh
                  </button>
                </div>

                {/* Location Name Input */}
                <div>
                  <label className="text-[11px] font-medium text-gray-500 block mb-1">
                    Location Name / Address
                  </label>
                  <input
                    type="text"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    placeholder="e.g. Head Office, Chennai"
                    className="w-full text-xs font-medium text-gray-800 bg-white border border-gray-200 rounded-lg p-2 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none"
                  />
                </div>

                {/* Coordinates & Accuracy Status */}
                <div className="pt-1">
                  {locationLoading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-600" />
                      Fetching high-accuracy coordinates...
                    </div>
                  ) : coords.latitude ? (
                    <div className="grid grid-cols-2 gap-2 text-[11px] bg-white p-2 rounded-lg border border-gray-100">
                      <div>
                        <span className="text-gray-400 block">Coordinates:</span>
                        <span className="font-mono text-gray-700 font-semibold truncate block">
                          {Number(coords.latitude).toFixed(5)}, {Number(coords.longitude).toFixed(5)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Accuracy:</span>
                        <span className="font-mono text-emerald-600 font-semibold block">
                          ±{coords.accuracy}m
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[11px] text-amber-600 flex items-start gap-1.5 bg-amber-50 p-2 rounded-lg border border-amber-100">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{locationError || "Location coordinates not detected."}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Work Mode Selection */}
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-2 flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-cyan-600" /> Work Mode
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "office", label: "Office", icon: Building },
                    { id: "remote", label: "Remote", icon: Home },
                    { id: "hybrid", label: "Client/Hybrid", icon: Globe },
                  ].map((m) => {
                    const Icon = m.icon;
                    const isActive = workMode === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setWorkMode(m.id)}
                        className={`py-2 px-2 rounded-xl text-xs font-semibold flex flex-col items-center justify-center gap-1 transition-all ${
                          isActive
                            ? "bg-cyan-50 border-2 border-cyan-500 text-cyan-700 shadow-xs"
                            : "bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${isActive ? "text-cyan-600" : "text-gray-400"}`} />
                        <span>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Note / Policy reminder */}
              <div className="text-[11px] text-gray-400 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                {faceVerificationRequired
                  ? "Selfie and location timestamp will be securely verified as per attendance policies."
                  : "Location timestamp will be securely recorded as per attendance policies."}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-200/60 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            // Disable submit only when face verification is required but no selfie yet
            disabled={isSubmitting || (faceVerificationRequired && !selfieFile)}
            className="px-5 py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-md hover:shadow-lg transition flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{isCheckOut ? "Checking Out..." : "Checking In..."}</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>{isCheckOut ? "Confirm Check-Out" : "Confirm Check-In"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export { CheckInModal, CheckInModal as CheckOutModal };
export default CheckInModal;
